import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Card, Button, Table, Tag, Modal, Space, Typography, message, Input, Select } from 'antd';
import { Link } from 'react-router-dom';
import * as Icon from 'react-feather';
import {
  communityActionStarts,
  communityActionErrors,
  getAllCommunitiesAction,
  deleteCommunityAction,
  joinCommunityAction,
  leaveCommunityAction,
} from '../../core/data/redux/modules/community/actions';
import { getAllCommunities, deleteCommunity, joinCommunity, leaveCommunity } from '../../services/communityServices';

const { Title } = Typography;
const { Option } = Select;

const ListCommunities = () => {
  const dispatch = useDispatch();
  const { communities, loading, error } = useSelector((state: any) => state.community || { communities: [], loading: false, error: null });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPrivacy, setFilterPrivacy] = useState('all');

  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    dispatch(communityActionStarts());
    try {
      const res = await getAllCommunities({ page: 1, page_size: 100 });
      if (res.error === true) throw new Error(res.message || 'Error fetching communities');
      const communitiesData = res.data.items || res.data;
      dispatch(getAllCommunitiesAction(communitiesData));
    } catch (err: any) {
      dispatch(communityActionErrors(err.message));
      message.error(err.message);
    }
  };

  const handleDelete = (community: any) => {
    setSelectedCommunity(community);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedCommunity) {
      try {
        await deleteCommunity(selectedCommunity.id);
        dispatch(deleteCommunityAction(selectedCommunity.id));
        message.success('Community deleted successfully');
        setDeleteModalOpen(false);
      } catch (err: any) {
        message.error(err.message);
      }
    }
  };

  const handleJoin = async (communityId: string) => {
    try {
      await joinCommunity(communityId);
      dispatch(joinCommunityAction(communityId));
      message.success('Joined community successfully');
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const handleLeave = async (communityId: string) => {
    try {
      await leaveCommunity(communityId);
      dispatch(leaveCommunityAction(communityId));
      message.success('Left community successfully');
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const filteredCommunities = (communities || []).filter((community: any) => {
    const matchesSearch = community.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      community.unique_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPrivacy = filterPrivacy === 'all' || 
      (filterPrivacy === 'private' ? community.is_private : !community.is_private);
    return matchesSearch && matchesPrivacy;
  });

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Unique Name',
      dataIndex: 'unique_name',
      key: 'unique_name',
    },
    {
      title: 'Owner',
      dataIndex: ['owner', 'username'],
      key: 'owner',
    },
    {
      title: 'Privacy',
      dataIndex: 'is_private',
      key: 'is_private',
      render: (isPrivate: boolean) => (
        <Tag color={isPrivate ? 'red' : 'green'}>
          {isPrivate ? 'Private' : 'Public'}
        </Tag>
      ),
    },
    {
      title: 'Members',
      dataIndex: 'member_count',
      key: 'member_count',
      render: (count: number) => count || 0,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, community: any) => (
        <Space>
          <Link to={`/admin/community-management/community-detail/${community.id}`}>
            <Button icon={<Icon.Eye size={16} />} size="small" />
          </Link>
          <Link to={`/admin/community-management/edit-community/${community.id}`}>
            <Button icon={<Icon.Edit size={16} />} size="small" />
          </Link>
          {community.is_member ? (
            <Button 
              icon={<Icon.LogOut size={16} />} 
              size="small" 
              onClick={() => handleLeave(community.id)}
            >
              Leave
            </Button>
          ) : (
            <Button 
              icon={<Icon.UserPlus size={16} />} 
              size="small" 
              type="primary"
              onClick={() => handleJoin(community.id)}
            >
              Join
            </Button>
          )}
          <Button 
            icon={<Icon.Trash2 size={16} />} 
            size="small" 
            danger 
            onClick={() => handleDelete(community)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/community-management">Community Management</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>List Communities</Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>Communities</Title>
            <Link to="/admin/community-management/create-community">
              <Button type="primary" icon={<Icon.PlusCircle size={16} />}>Create Community</Button>
            </Link>
          </div>
          
          <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Input
              placeholder="Search communities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
              prefix={<Icon.Search size={16} />}
            />
            <Select
              value={filterPrivacy}
              onChange={setFilterPrivacy}
              style={{ width: 120 }}
            >
              <Option value="all">All Privacy</Option>
              <Option value="public">Public</Option>
              <Option value="private">Private</Option>
            </Select>
          </div>

          <Table
            columns={columns}
            dataSource={filteredCommunities}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
        
        <Modal
          title="Confirm Delete"
          open={deleteModalOpen}
          onOk={confirmDelete}
          onCancel={() => setDeleteModalOpen(false)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <p>Are you sure you want to delete community <b>{selectedCommunity?.name}</b>?</p>
        </Modal>
      </div>
    </div>
  );
};

export default ListCommunities;
